import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Cpu, Globe, Users, Landmark, Handshake } from 'lucide-react';
import { partnershipsContent } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import useDocumentTitle from '../hooks/useDocumentTitle';

const PartnershipsPage = () => {
  const { language, t, pick } = useLanguage();
  useDocumentTitle(language === 'fr' ? 'Partenariats' : 'Partnerships');

  const partnerTypesByLang = {
    en: [
      { icon: Building2, title: 'Investors', desc: 'Long-term capital partners seeking exposure to African growth' },
      { icon: Cpu, title: 'Technology Partners', desc: 'Innovators bringing cutting-edge solutions to our ecosystem' },
      { icon: Globe, title: 'Infrastructure Operators', desc: 'Experienced operators scaling across sectors' },
      { icon: Landmark, title: 'Institutions', desc: 'Development finance and institutional investors' },
      { icon: Users, title: 'Governments', desc: 'Public sector partners driving national development' },
      { icon: Handshake, title: 'Strategic Partners', desc: 'Aligned organizations with complementary capabilities' }
    ],
    fr: [
      { icon: Building2, title: 'Investisseurs', desc: 'Partenaires capitalistiques long terme cherchant une exposition à la croissance africaine' },
      { icon: Cpu, title: 'Partenaires technologiques', desc: 'Innovateurs apportant des solutions de pointe à notre écosystème' },
      { icon: Globe, title: "Opérateurs d'infrastructure", desc: 'Opérateurs expérimentés capables de passer à l\'échelle' },
      { icon: Landmark, title: 'Institutions', desc: 'Finance de développement et investisseurs institutionnels' },
      { icon: Users, title: 'Gouvernements', desc: 'Partenaires du secteur public moteurs du développement national' },
      { icon: Handshake, title: 'Partenaires stratégiques', desc: 'Organisations alignées avec des capacités complémentaires' }
    ]
  };
  const partnerTypes = partnerTypesByLang[language];

  const principlesByLang = {
    en: ['Alignment of vision and values', 'Discipline in execution', 'Shared long-term objectives', 'Transparent communication', 'Mutual value creation'],
    fr: ['Alignement de la vision et des valeurs', "Discipline dans l'exécution", 'Objectifs partagés à long terme', 'Communication transparente', 'Création de valeur mutuelle']
  };
  const principles = principlesByLang[language];

  const e = {
    en: { whoWeWork: 'Who We Work With', categories: 'Partnership Categories', howPartner: 'How We Partner', partnerPrinciples: 'Partnership Principles', principlesIntro: 'Our partnerships are built on strong foundations. We seek partners who share our commitment to long-term value creation and responsible growth.', getStarted: 'Get Started', letsBuild: "Let's Build Together", ctaIntro: "Ready to explore partnership opportunities with Smartworld Group? We'd love to hear from you." },
    fr: { whoWeWork: 'Avec qui nous travaillons', categories: 'Catégories de partenariats', howPartner: 'Comment nous nous associons', partnerPrinciples: 'Principes de partenariat', principlesIntro: 'Nos partenariats reposent sur des bases solides. Nous recherchons des partenaires qui partagent notre engagement envers la création de valeur long terme et la croissance responsable.', getStarted: 'Commencer', letsBuild: 'Bâtissons ensemble', ctaIntro: 'Prêt à explorer les opportunités de partenariat avec Smartworld Group ? Nous serions ravis de vous lire.' }
  }[language];

  return (
    <div className="pt-20">
      <section className="section-padding-lg bg-[#1a1c1b] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute -top-20 -right-20 w-[500px] h-[500px] border border-[#5B7A8A] rounded-full"></div>
          <div className="absolute bottom-20 -left-20 w-[300px] h-[300px] border border-[#5B7A8A] rounded-full"></div>
        </div>
        <div className="container-swg relative z-10">
          <div className="max-w-4xl">
            <span className="caption text-[#5B7A8A] block mb-4">{pick(partnershipsContent.title)}</span>
            <h1 className="heading-1 text-white mb-8">{pick(partnershipsContent.headline)}</h1>
            <div className="accent-line mb-8"></div>
            <p className="body-large max-w-2xl mb-6">{pick(partnershipsContent.text)}</p>
            <p className="text-[#5B7A8A] font-medium text-lg">{pick(partnershipsContent.approach)}</p>
          </div>
        </div>
      </section>

      <section className="section-padding-lg bg-[#302f2c]/30 border-t border-[rgba(91,122,138,0.3)]">
        <div className="container-swg">
          <div className="text-center mb-16">
            <span className="caption text-[#5B7A8A] block mb-4">{e.whoWeWork}</span>
            <h2 className="heading-2 text-white">{e.categories}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {partnerTypes.map((type, index) => (
              <div key={index} className="swg-card rounded-xl p-8">
                <div className="w-14 h-14 bg-[#3d5a6a]/50 rounded-xl flex items-center justify-center mb-6">
                  <type.icon className="w-7 h-7 text-[#5B7A8A]" />
                </div>
                <h3 className="text-white font-semibold text-lg mb-3">{type.title}</h3>
                <p className="text-[#9AACB8] text-sm leading-relaxed">{type.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-[#1a1c1b]">
        <div className="container-swg">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="caption text-[#5B7A8A] block mb-4">{e.howPartner}</span>
              <h2 className="heading-2 text-white mb-6">{e.partnerPrinciples}</h2>
              <div className="accent-line mb-8"></div>
              <p className="body-large">{e.principlesIntro}</p>
            </div>
            <div className="space-y-4">
              {principles.map((principle, index) => (
                <div key={index} className="flex items-center gap-4 p-5 swg-card rounded-xl">
                  <div className="w-10 h-10 bg-[#3d5a6a] rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-[#5B7A8A] font-bold">{index + 1}</span>
                  </div>
                  <span className="text-white font-medium">{principle}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding-lg bg-[#3d5a6a]/20 border-y border-[rgba(91,122,138,0.5)]">
        <div className="container-swg">
          <div className="max-w-3xl mx-auto text-center">
            <span className="caption text-[#5B7A8A] block mb-4">{e.getStarted}</span>
            <h2 className="heading-2 text-white mb-8">{e.letsBuild}</h2>
            <p className="body-large mb-10">{e.ctaIntro}</p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/contact" className="btn-primary">
                {t('cta.contactUs')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
              <Link to="/ecosystem" className="btn-secondary">{t('cta.exploreEcosystem')}</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PartnershipsPage;
